import { useQuery } from '@tanstack/react-query';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontPaths, type StorefrontPaths } from '@/features/storefront/paths';

/**
 * Where a shelf lives: the same address the catalogue's own filter links use,
 * so a masthead link, a footer link and a shelf link all land identically.
 *
 * Takes the shop's paths rather than writing `/store` out, because the same
 * chrome renders both the merchant's live shop and a theme's static preview,
 * and a hardcoded prefix in here is a header link that walks a merchant out of
 * the preview they were exploring.
 */
export function categoryPath(paths: StorefrontPaths, slug: string) {
  return paths.products(slug);
}

/** The same, for a component that would rather not hold the paths itself. */
export function useCategoryPath(): (slug: string) => string {
  const paths = useStorefrontPaths();
  return (slug: string) => categoryPath(paths, slug);
}

/**
 * The shop's own shelves - top-level categories, in the order the merchant
 * arranged them, capped so no piece of chrome is overrun by a shop with fifty
 * of them.
 *
 * Shared by the header strip and the footer's category columns, and returns an
 * empty list rather than nothing at all for a shop with no categories, so a
 * caller laying out around it can count its columns before rendering them.
 *
 * `enabled` is off in the customizer preview, where no storefront host is
 * resolved and the request would ask about the wrong shop - or no shop.
 */
export function useShelfCategories({ limit, enabled = true }: { limit: number; enabled?: boolean }) {
  const source = useStorefrontData();
  const scope = useStorefrontScope();

  const categories = useQuery({
    queryKey: [...scope, 'categories'],
    queryFn: () => source.categories(),
    enabled,
  });

  return (categories.data ?? []).filter((category) => !category.parent_id).slice(0, limit);
}

/** A shelf and what the merchant has filed under it. */
export interface ShelfBranch {
  category: StorefrontCategory;
  children: StorefrontCategory[];
  /**
   * What is filed under each of those children, by the child's id - the
   * third level a mega menu lists under each column's heading. Empty lists
   * are left out.
   */
  grandchildren: Record<string, StorefrontCategory[]>;
}

/**
 * The same shelves, one level deeper: each department with its own
 * sub-departments hanging off it.
 *
 * The flat list is what a strip of links wants; a menu that opens - Tools &
 * Machine's department panel, the phone's sheet - wants the branch, because a
 * shopper looking for a rotary hammer is looking for it *under* hammer drills
 * rather than among forty shelves in one column.
 *
 * Same query as {@link useShelfCategories}, so a piece of chrome using both
 * asks the shop once. The cap counts departments, never their children: a
 * shelf's own sub-shelves are shown whole or the branch reads as truncated.
 */
export function useShelfCategoryTree({
  limit,
  enabled = true,
}: {
  limit: number;
  enabled?: boolean;
}): ShelfBranch[] {
  const source = useStorefrontData();
  const scope = useStorefrontScope();

  const categories = useQuery({
    queryKey: [...scope, 'categories'],
    queryFn: () => source.categories(),
    enabled,
  });

  const shelves = categories.data ?? [];
  return shelves
    .filter((category) => !category.parent_id)
    .slice(0, limit)
    .map((category) => {
      const children = shelves.filter((child) => child.parent_id === category.id);
      const grandchildren: Record<string, StorefrontCategory[]> = {};
      for (const child of children) {
        const under = shelves.filter((each) => each.parent_id === child.id);
        if (under.length > 0) grandchildren[child.id] = under;
      }
      return { category, children, grandchildren };
    });
}
